import React from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
  H5,
  Muted,
} from '@lb-classroom/ui';
import { X } from 'lucide-react';
import Textarea from '@components/UI/atoms/form/Textarea';
import RadioInputs from '@components/UI/atoms/form/RadioInputs';
import ModalMessage from '@components/UI/organisms/ModalMessage';
import type { TutorReportAndResolveIssueModalProps } from './tutor-report-and-resolve-issue-modal.types';

export const TutorReportAndResolveIssueModal = ({
  selectedIssueType,
  selectedAction,
  selectedReason,
  comment,
  issueTypeOptions,
  actionOptions,
  reasonOptions,
  isSubmitting,
  showReasonAndComment,
  showPendingInfo,
  submitButtonText,
  message,
  messageType,
  onIssueTypeChange,
  onActionChange,
  onReasonChange,
  onCommentChange,
  onSubmit,
  onCancel,
}: TutorReportAndResolveIssueModalProps) => {
  const issueTypeSelectProps = {
    options: issueTypeOptions,
    className: '',
    id: 'select-issue-type',
    value: selectedIssueType,
    name: 'report-and-resolve-type',
    updateHandler: onIssueTypeChange,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const actionSelectProps = {
    options: actionOptions,
    className: '',
    id: 'select-resolve-action',
    value: selectedAction,
    name: 'report-and-resolve-action',
    updateHandler: onActionChange,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const reasonSelectProps = {
    options: reasonOptions,
    className: '',
    id: 'report-and-resolve-reason',
    value: selectedReason,
    name: 'issue-reason',
    updateHandler: onReasonChange,
    invalidLabel: true,
    onKeyDown: onSubmit,
  };

  const commentProps = {
    rows: 5,
    value: comment,
    updateHandler: onCommentChange,
    className:
      'max-w-lg shadow-sm block w-full sm:text-sm border border-light-gray rounded-md py-2 px-4',
  };

  return (
    <div className="max-w-xl mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Report and Resolve Issue</CardTitle>
          <button type="button" className="modal-close" onClick={onCancel}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <ModalMessage message={message} messageType={messageType} />
          <form
            method="post"
            className="report-and-resolve-issue-form"
            onSubmit={onSubmit}
            noValidate
          >
            <div>
              <div className="issue-type-wrapper mb-4">
                <H5>Choose an Issue</H5>
                <RadioInputs {...issueTypeSelectProps} />
              </div>
              <Separator className="my-4" />
              <div className="actions-wrapper mb-4">
                <H5>How do you like to resolve the issue?</H5>
                <RadioInputs {...actionSelectProps} />
              </div>
              <Separator className="my-4" />
              {showPendingInfo && (
                <ul className="mt-6">
                  <li>
                    Please return a ticket instead if the reason is emtpy Skype
                    ID or incorrect timezone.
                  </li>
                  <li>
                    {
                      'The lesson status will be "Pending" status until 24 hours later of the lesson end time. Your student can report an issue in the mean time.'
                    }
                  </li>
                  <li>
                    At LingualBox, we pay full salary for skipped classes.
                    Optionally, you can return tickets to your student. In that
                    case, please confirm if the student has a valid reason
                    before returning tickets.
                  </li>
                </ul>
              )}
              {showReasonAndComment && (
                <>
                  <div className="reasons-wrapper mb-4">
                    <H5>What is the reason of the issue?</H5>
                    <RadioInputs {...reasonSelectProps} />
                  </div>
                  <Separator className="my-4" />
                  <div className="message-wrapper">
                    <H5 className="mb-4">Message to the student.</H5>
                    <Textarea {...commentProps} />
                    <Muted className="mt-2">
                      Please write the details of the reason and apology message
                      to your student. For tracking purpose, both the student
                      and LingualBox staff will be notified of the reason.
                    </Muted>
                  </div>
                </>
              )}
            </div>
            <Separator className="my-4" />
            <div className="text-right">
              <Button disabled={isSubmitting} size="lg" type="submit">
                {submitButtonText}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
